import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/auth";
import NexWordmark from "@/components/NexWordmark";
import { LIFE_LOGO } from "@/lib/assets";
import { ArrowLeft, Plus, Trash2, Save, ExternalLink, Copy, CheckCheck, Loader2, Eye, Lock, Layers, Send, Mail, MessageSquare, Sparkles, Play, Film } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = ["in_progress", "review", "delivered", "archived"];

const initialForm = {
  client_name: "",
  business_name: "",
  slug: "",
  preview_url: "",
  access_code: "",
  status: "in_progress",
  notes: "",
};

const PreviewManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editing, setEditing] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/previews");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setForm(initialForm);
    setEditing(null);
    setShowForm(false);
  };

  const startCreate = () => {
    setForm(initialForm);
    setEditing(null);
    setShowForm(true);
  };

  const startEdit = (item) => {
    setForm({
      client_name: item.client_name,
      business_name: item.business_name,
      slug: item.slug,
      preview_url: item.preview_url || "",
      access_code: item.access_code || "",
      status: item.status || "in_progress",
      notes: item.notes || "",
    });
    setEditing(item.id);
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await apiClient.patch(`/admin/previews/${editing}`, form);
        toast.success("Preview updated");
      } else {
        await apiClient.post("/admin/previews", form);
        toast.success("Preview created");
      }
      resetForm();
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (id) => {
    if (!window.confirm("Delete this preview slot? The link will stop working immediately.")) return;
    try {
      await apiClient.delete(`/admin/previews/${id}`);
      toast.success("Preview deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyLink = (item) => {
    const url = `${window.location.origin}/preview/${item.slug}${item.access_code ? `?code=${item.access_code}` : ""}`;
    navigator.clipboard.writeText(url);
    setCopiedId(item.id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success("Link copied");
  };

  return (
    <div className="min-h-screen bg-[#0B0F19]">
      {/* Top bar */}
      <div className="border-b border-white/10 bg-[#0A0E17] sticky top-0 z-40 backdrop-blur-xl">
        <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-4">
            <img src={LIFE_LOGO} alt="" className="h-9 w-9 object-contain" />
            <div className="w-px h-8 bg-nex-border" />
            <NexWordmark size="text-xl" />
          </Link>
          <Link to="/admin" data-testid="back-to-dashboard" className="tick-btn-secondary text-xs !py-2 !px-3">
            <ArrowLeft size={13} /> Dashboard
          </Link>
        </div>
      </div>

      <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 py-10 space-y-6">

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <div className="mono-label mb-3">// Prototype Preview Manager</div>
            <h1 className="serif-head text-4xl md:text-5xl">Private client previews.</h1>
            <p className="text-nex-chrome mt-3 max-w-2xl">Create a private URL for each client. Paste your prototype URL, add an optional access code, and share one link. They see progress — nobody else does.</p>
          </div>
          <button onClick={startCreate} data-testid="new-preview-btn" className="tick-btn-primary">
            <Plus size={16} /> New Preview
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <form onSubmit={submit} className="card-industrial corner-tick p-6 space-y-5">
            <div className="mono-label">{editing ? "// Editing preview" : "// New preview"}</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="mono-label block mb-2">Client Name</label>
                <input data-testid="pf-client" required type="text" value={form.client_name} onChange={(e) => setForm({...form, client_name: e.target.value})} className="input-industrial" placeholder="John Doe" />
              </div>
              <div>
                <label className="mono-label block mb-2">Business Name</label>
                <input data-testid="pf-business" required type="text" value={form.business_name} onChange={(e) => setForm({...form, business_name: e.target.value})} className="input-industrial" placeholder="Wired SC" />
              </div>
              <div>
                <label className="mono-label block mb-2">Slug <span className="text-nex-muted">(leave blank to auto-generate)</span></label>
                <input data-testid="pf-slug" type="text" value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} className="input-industrial" placeholder="wired-sc-flow" disabled={!!editing} />
              </div>
              <div>
                <label className="mono-label block mb-2">Access Code <span className="text-nex-muted">(optional)</span></label>
                <input data-testid="pf-access" type="text" value={form.access_code} onChange={(e) => setForm({...form, access_code: e.target.value})} className="input-industrial" placeholder="secret code the client will enter" />
              </div>
              <div className="md:col-span-2">
                <label className="mono-label block mb-2">Preview URL (where the actual prototype lives)</label>
                <input data-testid="pf-url" type="url" value={form.preview_url} onChange={(e) => setForm({...form, preview_url: e.target.value})} className="input-industrial" placeholder="https://your-prototype.emergent.host" />
              </div>
              <div>
                <label className="mono-label block mb-2">Status</label>
                <div className="flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map((s) => (
                    <button key={s} type="button" data-testid={`pf-status-${s}`} onClick={() => setForm({...form, status: s})} className={`px-3 py-2 text-[12px] mono-body uppercase tracking-widest border transition-colors ${form.status === s ? "border-nex-gold text-nex-gold bg-nex-gold/10" : "border-white/10 text-nex-silver hover:border-white/30"}`}>
                      {s.replace("_", " ")}
                    </button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="mono-label block mb-2">Notes <span className="text-nex-muted">(client-facing progress notes)</span></label>
                <textarea data-testid="pf-notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={3} className="input-industrial resize-none" placeholder="What's new this iteration, what's next, blockers you need input on..." />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={resetForm} className="tick-btn-secondary">Cancel</button>
              <button type="submit" disabled={busy} data-testid="pf-submit" className="tick-btn-primary disabled:opacity-50">
                {busy ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : <><Save size={14} /> {editing ? "Update" : "Create"}</>}
              </button>
            </div>
          </form>
        )}

        {/* Items */}
        {loading ? (
          <div className="py-20 text-center mono-body text-[12px] text-nex-chrome">
            <Loader2 size={16} className="animate-spin inline mr-2" /> Loading previews...
          </div>
        ) : items.length === 0 ? (
          <div className="py-20 text-center">
            <Layers size={32} className="mx-auto text-nex-muted mb-3" />
            <div className="mono-body text-[12px] text-nex-chrome">No prototype previews yet. Create your first one above.</div>
          </div>
        ) : (
          <div className="grid gap-4">
            {items.map((it) => (
              <div key={it.id} data-testid={`preview-${it.id}`} className="card-industrial p-5">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <h3 className="serif-head text-xl text-nex-silver">{it.business_name}</h3>
                      <span className="mono-body text-[9px] uppercase tracking-widest px-2 py-1 border border-nex-gold/40 text-nex-gold bg-nex-gold/5">{it.status?.replace("_", " ")}</span>
                      {it.access_code && (
                        <span className="inline-flex items-center gap-1 mono-body text-[9px] uppercase tracking-widest px-2 py-1 border border-white/10 text-nex-chrome">
                          <Lock size={9} /> code-gated
                        </span>
                      )}
                    </div>
                    <div className="mono-body text-[11px] mb-2">{it.client_name}</div>
                    <div className="mono-body text-[10px] text-nex-gold">/preview/{it.slug}</div>
                    {it.preview_url && (
                      <div className="mono-body text-[10px] mt-1 truncate">→ {it.preview_url}</div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => copyLink(it)} data-testid={`copy-${it.id}`} className="tick-btn-secondary !py-2 !px-3 text-xs">
                      {copiedId === it.id ? <><CheckCheck size={12} /> Copied</> : <><Copy size={12} /> Copy Link</>}
                    </button>
                    <a href={`/preview/${it.slug}${it.access_code ? `?code=${it.access_code}` : ""}`} target="_blank" rel="noreferrer" data-testid={`open-${it.id}`} className="tick-btn-secondary !py-2 !px-3 text-xs">
                      <Eye size={12} /> Preview
                    </a>
                    <button onClick={() => startEdit(it)} data-testid={`edit-${it.id}`} className="tick-btn-secondary !py-2 !px-3 text-xs">
                      Edit
                    </button>
                    <button onClick={() => doDelete(it.id)} data-testid={`delete-${it.id}`} className="tick-btn-secondary !py-2 !px-3 text-xs !border-red-500/40 !text-red-400">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
                {it.notes && (
                  <div className="mt-4 pt-4 border-t border-white/5">
                    <div className="mono-body text-[10px] uppercase tracking-widest text-nex-muted mb-1">// notes</div>
                    <p className="text-nex-silver text-sm whitespace-pre-wrap">{it.notes}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PreviewManager;
